// Spec 30 PR 3 cleanup Phase 3 (T2) — TextPrompt.
//
// One open question + textarea answer. Submitting / succeeded /
// failed states follow the same compress-to-trace pattern as
// WorkspaceConfirm; idle and dirty render the full layout.

import { ArrowRight, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Pill } from "@/components/glass";

import type { WidgetRenderProps } from "../registry";

interface TextPromptProps {
	question: string;
	body?: string | null;
	placeholder: string;
	minLength: number;
	maxLength: number;
	confirmLabel: string;
}

export function TextPrompt({
	props,
	state,
	onAction,
}: WidgetRenderProps<TextPromptProps>) {
	const [value, setValue] = useState("");
	const ref = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		ref.current?.focus();
	}, []);

	const submitting = state.kind === "submitting";
	const succeeded = state.kind === "succeeded";
	const failed = state.kind === "failed";

	if (succeeded) {
		const result = state.result as { text?: string } | undefined;
		const final = result?.text ?? value;
		return (
			<div className="text-center py-2">
				<span className="text-[12px] text-[var(--color-text-muted)]">
					<span className="text-[var(--color-success)] mr-1.5">✓</span>
					<span className="opacity-90">
						{final.length > 80 ? `${final.slice(0, 77)}…` : final}
					</span>
				</span>
			</div>
		);
	}

	const trimmed = value.trim();
	const tooShort = trimmed.length > 0 && trimmed.length < props.minLength;
	const ready = trimmed.length >= props.minLength && !submitting;

	const submit = () => {
		if (!ready) return;
		onAction("confirm", { text: trimmed });
	};

	return (
		<div
			className="py-10 space-y-6"
			style={{ opacity: submitting ? 0.8 : 1, transition: "opacity 200ms" }}
		>
			<div className="text-center space-y-2">
				<div className="text-[24px] font-medium tracking-tight text-[var(--color-text)] anim-fade-rise">
					{props.question}
				</div>
				{props.body && (
					<div
						className="text-[13px] text-[var(--color-text-muted)] max-w-md mx-auto leading-relaxed anim-fade-in"
						style={{ animationDelay: "120ms" }}
					>
						{props.body}
					</div>
				)}
			</div>

			<div
				className="max-w-[520px] mx-auto space-y-2 anim-fade-rise"
				style={{ animationDelay: "200ms" }}
			>
				<textarea
					ref={ref}
					value={value}
					onChange={(e) => setValue(e.target.value.slice(0, props.maxLength))}
					onKeyDown={(e) => {
						if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
							e.preventDefault();
							submit();
						}
					}}
					placeholder={props.placeholder}
					disabled={submitting}
					rows={3}
					className="w-full px-4 py-3 rounded-md bg-[var(--color-surface-solid)] border border-[var(--color-border)] text-[14px] leading-relaxed outline-none hover:border-[var(--color-border-hi)] focus:border-[var(--color-border-hi)] disabled:opacity-40 resize-none"
				/>
				<div className="flex items-center justify-between text-[10px]">
					<span className="text-[var(--color-text-dim)]">
						{tooShort
							? `at least ${props.minLength} chars`
							: failed
								? "Try again"
								: " "}
					</span>
					<span className="text-[var(--color-text-dim)] mono">
						{value.length} / {props.maxLength}
					</span>
				</div>
			</div>

			<div className="flex justify-center">
				<Pill
					onClick={submit}
					disabled={!ready}
					className="!h-11 !px-5 !text-[14px]"
				>
					{submitting ? (
						<Loader2 className="w-4 h-4 animate-spin" />
					) : (
						<>
							{props.confirmLabel}
							<ArrowRight className="w-4 h-4" />
						</>
					)}
				</Pill>
			</div>
		</div>
	);
}
